import Config from '@config';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  CreateGenericReportTemplateRequest,
  GenericReportTemplateDetailsResult,
  GenericReportTemplateSummaryResult,
  UpdateGenericReportTemplateRequest,
} from './types';

export const reportTemplatesApi = createApi({
  reducerPath: 'reportTemplatesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/reports/report-templates',
  }),
  tagTypes: ['ReportTemplates', 'ReportTemplate'],
  endpoints: builder => ({
    getReportTemplatesI: builder.infiniteQuery<
      GenericReportTemplateSummaryResult[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['ReportTemplates'],
    }),

    getReportTemplateById: builder.query<
      GenericReportTemplateDetailsResult,
      string
    >({
      query: id => ({ url: `/${id}` }),
      providesTags: (_, __, id) => [{ type: 'ReportTemplate', id }],
    }),

    createReportTemplate: builder.mutation<
      string,
      CreateGenericReportTemplateRequest
    >({
      query: data => ({
        url: '',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ReportTemplates'],
    }),

    updateReportTemplate: builder.mutation<
      void,
      UpdateGenericReportTemplateRequest
    >({
      query: ({ id, ...data }) => ({
        url: `/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_, __, { id }) => [
        'ReportTemplates',
        { type: 'ReportTemplate', id },
      ],
    }),

    deleteReportTemplate: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ReportTemplates'],
    }),
  }),
});

export const {
  useGetReportTemplateByIdQuery,
  useCreateReportTemplateMutation,
  useUpdateReportTemplateMutation,
  useDeleteReportTemplateMutation,
} = reportTemplatesApi;

export const useGetReportTemplatesIInfiniteQuery = normalizeInfiniteQueryData(
  reportTemplatesApi.useGetReportTemplatesIInfiniteQuery,
);
